import { memo, useCallback, useEffect, useState } from "react";


import { StorageWrapper } from "@/lib/aws-amplify";
import { uploadPublicImg } from "@/lib/aws-amplify/host-storage";
import type { S3Object } from "@/lib/gql/__generated__/graphql";
import { isEqual } from "@/lib/loadash/is-equal";

import { InputForm } from "@/components/ui/input/image/parts/input-form";
import { InputImageWrapper } from "@/components/ui/input/image/parts/input-image-wrapper";
import { PreviewItems } from "@/components/ui/input/image/parts/preview-items";
import { CircleLoading } from "@/components/ui/loading/circle";

import { FormErrorMessage, FormItem, FormLabel } from "../../../_base/form";

import type { ReactNode } from "react";

type MultipleImageFieldProps = {
	images: S3Object[] | null | undefined;

	onSetKeyVisuals: (props: S3Object[]) => void;
	ownerID: string;
	resourceID: string;
	errorMessage: string | undefined;

	label?: ReactNode;

	uploadPublicImgProps?: { path: string; fileName: string };
};

export const MultipleImageFieldNew = memo(
	({
		images: _images,
		onSetKeyVisuals,
		errorMessage,
		ownerID,
		resourceID,
		uploadPublicImgProps,
		label,
	}: MultipleImageFieldProps): JSX.Element | null => {
		useEffect(() => {
			setImages((prev) => {
				if (prev.length && !_images?.length) {
					return [];
				}
				return prev;
			});
		}, [_images]);

		const [images, setImages] = useState<S3Object[]>(_images ?? []);
		const [isUploading, setIsUploading] = useState(false);

		const handleUpload = useCallback(
			async (files: FileList | null) => {
				if (!files?.length) {
					return;
				}

				setIsUploading(true);

				const file = files.item(0)!;

				const res = uploadPublicImgProps
					? {
							s3Object: await uploadPublicImg(
								uploadPublicImgProps.path,
								uploadPublicImgProps.fileName,
								file,
							),
						}
					: await StorageWrapper.postImage({
							file,
							ownerID,
							resourceID,
						});

				onSetKeyVisuals([...images, res.s3Object]);
				setImages([...images, res.s3Object]);
				setIsUploading(false);
			},
			[images, onSetKeyVisuals, ownerID, resourceID, uploadPublicImgProps],
		);

		const handleChange = useCallback(
			async (e: React.ChangeEvent<HTMLInputElement>) => {
				const { files } = e.target;

				if (!files?.length) {
					return;
				}

				await handleUpload(files);
			},
			[handleUpload],
		);

		const handleClick = useCallback(
			(item: S3Object) => {
				const newImages = images.filter((image) => !isEqual(image, item));

				onSetKeyVisuals([...newImages]);
				setImages([...newImages]);
			},
			[images, onSetKeyVisuals],
		);

		const [isDragging, setIsDragging] = useState(false);
		const handleDrop = useCallback(
			async (e: React.DragEvent<HTMLLabelElement>) => {
				e.preventDefault();
				setIsDragging(false);
				const { files } = e.dataTransfer;

				if (!files?.length) {
					return;
				}

				await handleUpload(files);
			},
			[handleUpload],
		);

		const handleDragEnter = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
			e.preventDefault();
			setIsDragging(true);
		}, []);

		const handleDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
			e.preventDefault();
			setIsDragging(false);
		}, []);

		const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
			e.preventDefault();
			setIsDragging(true);
		}, []);

		return (
			<FormItem>
				{Boolean(label) && <FormLabel>{label}</FormLabel>}
				{isUploading && <CircleLoading size="sm" />}

				<InputImageWrapper>
					<InputForm
						isDragging={isDragging}
						handleDragEnter={handleDragEnter}
						handleDragLeave={handleDragLeave}
						handleDragOver={handleDragOver}
						handleDrop={handleDrop}
						handleChange={handleChange}
					/>
					<PreviewItems items={images} handleClick={handleClick} />
				</InputImageWrapper>
				<FormErrorMessage>{errorMessage}</FormErrorMessage>
			</FormItem>
		);
	},
);

MultipleImageFieldNew.displayName = "MultipleImageFieldNew";
